import { Lens } from '@dhmk/zustand-lens';

import { AddFundDocumentModalProps } from '@/app/global-modals/add-fund-document-modal';
import { AddressBookCategoriesModalProps } from '@/app/global-modals/address-book-categories-modal';
import { BlockActionModalProps } from '@/app/global-modals/block-action-modal';
import { ConfirmActionModalProps } from '@/app/global-modals/confirm-action-modal';
import { CustomRangeProps } from '@/app/global-modals/custom-range-modal';
import { CustomizeImgModalProps } from '@/app/global-modals/customize-img-modal';
import { DeleteDealCompanyModalProps } from '@/app/global-modals/delete-deal-company-modal';
import { DemandEntryModalProps } from '@/app/global-modals/demand-entry-modal';
import { DemandRemoveModalProps } from '@/app/global-modals/demand-remove-modal';
import { EntityStatusAlertModalProps } from '@/app/global-modals/entity-status-alert-modal';
import { FundJobTitleEditModalProps } from '@/app/global-modals/fund-job-title-edit-modal';
import { FundRemoveUserModalProps } from '@/app/global-modals/fund-remove-user-modal';
import { FundDescriptionProps } from '@/app/global-modals/funds-description-modal';
import { GammaTeamMemberDeleteProps } from '@/app/global-modals/gamma-team-member-delete';
import { GammaTeamMemberEditData } from '@/app/global-modals/gamma-team-member-edit';
import { HeadShotModalProps } from '@/app/global-modals/headshot-modal';
import { NotificationModalProps } from '@/app/global-modals/notification-modal';
import { ProfileRequirementsModalProps } from '@/app/global-modals/profile-requirements-modal';
import { RejectActionModalProps } from '@/app/global-modals/reject-action-modal';
import { RejectModalProps } from '@/app/global-modals/reject-modal';
import { ReplaceFundDocumentModalProps } from '@/app/global-modals/replace-fund-document-modal';
import { RestoreActionModalProps } from '@/app/global-modals/restore-action-modal';
import { SignDocumentModalProps } from '@/app/global-modals/sign-document-modal';
import { TableLegendModalProps } from '@/app/global-modals/table-legend-modal';
import { TeamMemberAddProps } from '@/app/global-modals/team-member-add';
import { TeamMemberDeleteProps } from '@/app/global-modals/team-member-delete';
import { TeamMemberEditData } from '@/app/global-modals/team-member-edit';

export type GlobalModalProps = {
  ADDRESS_BOOK_CATEGORIES_MODAL: AddressBookCategoriesModalProps;
  KYC_START_MODAL: null;
  ADD_FUND_DOCUMENT_MODAL: AddFundDocumentModalProps;
  REPLACE_FUND_DOCUMENT_MODAL: ReplaceFundDocumentModalProps;
  SETUP_FUND_MODAL: null;
  TEAM_MEMBER_ADD_MODAL: TeamMemberAddProps;
  TEAM_MEMBER_EDIT_MODAL: TeamMemberEditData;
  TEAM_MEMBER_DELETE_MODAL: TeamMemberDeleteProps;
  GAMMA_TEAM_MEMBER_ADD_MODAL: null;
  GAMMA_TEAM_MEMBER_EDIT_MODAL: GammaTeamMemberEditData;
  GAMMA_TEAM_MEMBER_DELETE_MODAL: GammaTeamMemberDeleteProps;
  FUND_DESCRIPTION_MODAL: FundDescriptionProps;
  HEADSHOT_MODAL: HeadShotModalProps;
  CUSTOM_RANGE_MODAL: CustomRangeProps;
  FUND_JOB_TITLE_EDIT_MODAL: FundJobTitleEditModalProps;
  FUND_REMOVE_USER_MODAL: FundRemoveUserModalProps;
  NOTIFICATION_MODAL: NotificationModalProps;
  CREATE_NEW_FUND_MODAL: null;
  TABLE_LEGEND_MODAL: TableLegendModalProps;
  SIGN_DOCUMENT_MODAL: SignDocumentModalProps;
  CUSTOMIZE_IMG_MODAL: CustomizeImgModalProps;
  CONFIRM_ACTION_MODAL: ConfirmActionModalProps;
  BLOCK_ACTION_MODAL: BlockActionModalProps;
  QR_CODE_MODAL: null;
  REJECT_ACTION_MODAL: RejectActionModalProps;
  RESTORE_ACTION_MODAL: RestoreActionModalProps;
  REJECT_ENTITY_AFFILIATION: RejectModalProps;
  ENTITY_STATUS_ALERT_MODAL: EntityStatusAlertModalProps;
  DELETE_DEAL_COMPANY_MODAL: DeleteDealCompanyModalProps;
  MANUAL_ATTRIBUTION_MODAL: null;
  DEMAND_ENTRY_MODAL: DemandEntryModalProps;
  DEMAND_REMOVE_MODAL: DemandRemoveModalProps;
  PROFILE_REQUIREMENTS_MODAL: ProfileRequirementsModalProps;
};

export type GlobalModalsKeys = keyof GlobalModalProps;

export type GlobalModalsSlice = {
  globalModals: typeof globalModals;
  handleOpen: (_modalKey: GlobalModalsKeys, _props?: GlobalModalProps[GlobalModalsKeys]) => void;
  handleClose: (_modalKey: GlobalModalsKeys) => void;
  props: GlobalModalProps[GlobalModalsKeys] | null;
};

const globalModals: Record<GlobalModalsKeys, boolean> = {
  ADDRESS_BOOK_CATEGORIES_MODAL: false,
  KYC_START_MODAL: false,
  ADD_FUND_DOCUMENT_MODAL: false,
  REPLACE_FUND_DOCUMENT_MODAL: false,
  SETUP_FUND_MODAL: false,
  TEAM_MEMBER_ADD_MODAL: false,
  TEAM_MEMBER_EDIT_MODAL: false,
  TEAM_MEMBER_DELETE_MODAL: false,
  GAMMA_TEAM_MEMBER_ADD_MODAL: false,
  GAMMA_TEAM_MEMBER_EDIT_MODAL: false,
  GAMMA_TEAM_MEMBER_DELETE_MODAL: false,
  FUND_DESCRIPTION_MODAL: false,
  HEADSHOT_MODAL: false,
  CUSTOM_RANGE_MODAL: false,
  FUND_JOB_TITLE_EDIT_MODAL: false,
  FUND_REMOVE_USER_MODAL: false,
  NOTIFICATION_MODAL: false,
  CREATE_NEW_FUND_MODAL: false,
  TABLE_LEGEND_MODAL: false,
  SIGN_DOCUMENT_MODAL: false,
  CUSTOMIZE_IMG_MODAL: false,
  CONFIRM_ACTION_MODAL: false,
  BLOCK_ACTION_MODAL: false,
  QR_CODE_MODAL: false,
  REJECT_ACTION_MODAL: false,
  RESTORE_ACTION_MODAL: false,
  REJECT_ENTITY_AFFILIATION: false,
  ENTITY_STATUS_ALERT_MODAL: false,
  DELETE_DEAL_COMPANY_MODAL: false,
  MANUAL_ATTRIBUTION_MODAL: false,
  DEMAND_ENTRY_MODAL: false,
  DEMAND_REMOVE_MODAL: false,
  PROFILE_REQUIREMENTS_MODAL: false,
};

export const createGlobalModalsSlice: Lens<GlobalModalsSlice> = (set) => ({
  globalModals,
  props: null,
  handleOpen: (modalKey, props) =>
    set((state) => {
      if (props) {
        state.props = props;
      }
      state.globalModals[modalKey] = true;
    }),

  handleClose: (modalKey) =>
    set((state) => {
      state.props = null;
      state.globalModals[modalKey] = false;
    }),
});
